import { useEffect, useRef, useState } from "react";

const DEFAULT_CHAR_DELAY_MS = 30;
const DEFAULT_PAUSE_BETWEEN_TEXTS_MS = 1800;

type Phase = "typing" | "pause";

type Params = {
    texts: string[];
    charDelayMs?: number;
    pauseBetweenTextsMs?: number;
};

export const usePlaceholderTypingAnimation = ({
    texts,
    charDelayMs = DEFAULT_CHAR_DELAY_MS,
    pauseBetweenTextsMs = DEFAULT_PAUSE_BETWEEN_TEXTS_MS,
}: Params): string => {
    const [displayedText, setDisplayedText] = useState("");
    const [phase, setPhase] = useState<Phase>("typing");
    const [textIndex, setTextIndex] = useState(0);

    const charIndexRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (texts.length === 0) {
            setDisplayedText("");
            return;
        }

        if (phase === "typing") {
            const textToType = texts[textIndex] ?? "";
            if (!textToType) {
                setPhase("pause");
                setTextIndex(0);
                return;
            }
            charIndexRef.current = 0;
            setDisplayedText("");

            const typeNextChar = () => {
                if (charIndexRef.current >= textToType.length) {
                    timerRef.current = null;
                    const nextIndex = (textIndex + 1) % texts.length;
                    setTextIndex(nextIndex);
                    setPhase("pause");
                    return;
                }
                const nextChar = textToType[charIndexRef.current];
                charIndexRef.current += 1;
                setDisplayedText((prev) => prev + nextChar);
                timerRef.current = setTimeout(typeNextChar, charDelayMs);
            };

            timerRef.current = setTimeout(typeNextChar, charDelayMs);
        } else if (phase === "pause") {
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                setPhase("typing");
                setDisplayedText("");
                charIndexRef.current = 0;
            }, pauseBetweenTextsMs);
        }

        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
        };
    }, [phase, texts, textIndex, charDelayMs, pauseBetweenTextsMs]);

    return displayedText;
};
